// Simple WebSocket-based task status service
// Backend websocket path (include `multi` API prefix)
const DEFAULT_WS_PATH = "/api/v1/multi/ws";

function normalizeTaskKey(task) {
  const t = (task || "").toLowerCase();
  if (t.includes("sync") || t.includes("do_sync")) return "sync";
  if (
    t.includes("recalc") ||
    t.includes("recalculate") ||
    t.includes("recalculate_articles") ||
    t.includes("monitor") ||
    t.includes("price")
  ) {
    return "recalc";
  }
  if (t.includes("export")) return "export";
  return (task || "unknown").toLowerCase();
}

function taskStatusKey(source, task) {
  return `${(source || "otto").toLowerCase()}:${normalizeTaskKey(task)}`;
}

class TaskStatusService {
  constructor() {
    this.ws = null;
    this.callbacks = new Set();
    this.finishCallbacks = new Set();
    this.itemUpdateCallbacks = new Set();
    this.running = new Map(); // key -> payload
    this.reconnectInterval = 3000;
    this.started = false;
  }

  _getUrl() {
    const proto = window.location.protocol === "https:" ? "wss://" : "ws://";
    // In development the frontend runs on :3000 and backend on :8000.
    // If running on localhost, prefer backend at port 8000 so Vite's dev server
    // (which may not proxy WS) isn't used for the websocket connection.
    const hostname = window.location.hostname;
    const useHost =
      hostname === "localhost" || hostname === "127.0.0.1"
        ? hostname + ":8001"
        : window.location.host;
    return proto + useHost + DEFAULT_WS_PATH;
  }

  start() {
    if (this.started) return;
    this.started = true;
    this._connect();
  }

  _connect() {
    try {
      const url = this._getUrl();
      this.ws = new WebSocket(url);
      this.ws.onopen = () => {
        console.log("TaskStatus WS open", url);
      };
      this.ws.onmessage = (ev) => {
        try {
          console.log("TaskStatus WS raw message", ev.data);
        } catch (e) {}
        this._onMessage(ev);
      };
      this.ws.onclose = (ev) => {
        console.debug(
          "TaskStatus WS closed, reconnecting...",
          ev.code,
          ev.reason,
        );
        setTimeout(() => this._connect(), this.reconnectInterval);
      };
      this.ws.onerror = (err) => {
        console.warn("TaskStatus WS error", err);
        try {
          this.ws.close();
        } catch (e) {}
      };
    } catch (e) {
      console.warn("Failed to start TaskStatus WS", e);
      setTimeout(() => this._connect(), this.reconnectInterval);
    }
  }

  _onMessage(ev) {
    let msg;
    try {
      msg = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
      console.log("TaskStatus WS parsed message", msg);
    } catch (e) {
      console.warn("Invalid WS message", e);
      return;
    }

    if (msg && msg.type === "task_status") {
      const key = taskStatusKey(msg.source, msg.task || msg.name);
      if (msg.active) {
        console.log("TaskStatus running set", key, msg);
        this.running.set(key, msg);
      } else {
        console.log("TaskStatus running cleared", key);
        this.running.delete(key);
        this._emitFinish(msg);
      }
      this._emit();
    } else if (msg && msg.type === "item_update") {
      this._emitItemUpdate(msg);
    } else if (msg && msg.type === "task_status_snapshot") {
      console.log("TaskStatus WS received snapshot", msg.data);
      this.running.clear(); // Clear all stale tasks!
      if (Array.isArray(msg.data)) {
        msg.data.forEach((st) => {
          this.running.set(taskStatusKey(st.source, st.task || st.name), st);
        });
      }
      this._emit();
    }
  }

  _emit() {
    const snapshot = Array.from(this.running.values());
    console.log("TaskStatus emitting snapshot", snapshot);
    for (const cb of this.callbacks) {
      try {
        cb(snapshot);
      } catch (e) {
        console.warn("TaskStatus callback error", e);
      }
    }
  }

  _emitFinish(msg) {
    for (const cb of this.finishCallbacks) {
      try {
        cb(msg);
      } catch (e) {
        console.warn("TaskStatus finish callback error", e);
      }
    }
  }

  _emitItemUpdate(msg) {
    for (const cb of this.itemUpdateCallbacks) {
      try {
        cb(msg);
      } catch (e) {
        console.warn("TaskStatus item_update callback error", e);
      }
    }
  }

  subscribe(cb) {
    this.callbacks.add(cb);
    // send current snapshot immediately
    const snap = Array.from(this.running.values());
    console.log("TaskStatus subscribe, sending snapshot", snap);
    cb(snap);
    this.start();
    return () => this.callbacks.delete(cb);
  }

  subscribeFinish(cb) {
    this.finishCallbacks.add(cb);
    this.start();
    return () => this.finishCallbacks.delete(cb);
  }

  subscribeItemUpdate(cb) {
    this.itemUpdateCallbacks.add(cb);
    this.start();
    return () => this.itemUpdateCallbacks.delete(cb);
  }

  getRunning() {
    return Array.from(this.running.values());
  }
}

export { normalizeTaskKey };

const taskStatusService = new TaskStatusService();
export default taskStatusService;
